import { useEffect, useMemo, useState } from "react";
import { diffLines } from "diff";
import { ShieldCheck } from "lucide-react";
import { useApi, fmtDate } from "@/lib/api";
import { t } from "@/i18n";

const label = (v) => (v.version === "draft" ? t("Current draft (unpublished)") : `v${v.version} · ${fmtDate(v.published_at)}`);

function Approval({ v }) {
  if (!v || v.version === "draft") return <p className="text-xs text-[var(--status-info)]">{t("Not yet approved")}</p>;
  return (
    <p className="text-xs faint flex items-center gap-1" data-testid={`version-approval-${v.version}`}>
      <ShieldCheck size={12} className="text-[var(--accent-primary)]" /> {t("Approved by {name} on {date}", { name: v.approved_by_name || t("owner"), date: fmtDate(v.published_at) })}
      {v.note && <> · “{v.note}”</>}
    </p>
  );
}

function DiffPane({ parts, side }) {
  return (
    <pre className="text-xs whitespace-pre-wrap font-mono leading-relaxed bg-[var(--bg-primary)] rounded p-3 max-h-[60vh] overflow-auto" data-testid={`version-pane-${side}`}>
      {parts.map((p, i) => {
        if ((side === "left" && p.added) || (side === "right" && p.removed)) return null;
        const cls = p.added ? "bg-emerald-500/20 text-[var(--text-primary)]" : p.removed ? "bg-rose-500/20 line-through decoration-rose-400/60" : "faint";
        return <span key={i} className={cls}>{p.value}</span>;
      })}
    </pre>
  );
}

export function VersionHistory({ sopId }) {
  const api = useApi();
  const [all, setAll] = useState(null);
  const [left, setLeft] = useState(0);
  const [right, setRight] = useState(0);

  useEffect(() => {
    api.get(`/sops/${sopId}/versions`).then((r) => {
      const list = [...(r.data.draft ? [r.data.draft] : []), ...r.data.versions];
      setAll(list);
      setRight(0);
      setLeft(Math.min(1, list.length - 1));
    });
  }, [api, sopId]);

  const parts = useMemo(() => (all && all.length ? diffLines(all[left].markdown, all[right].markdown) : []), [all, left, right]);
  if (!all) return <p className="faint">{t("Loading history…")}</p>;
  if (all.length === 0) return <p className="muted" data-testid="versions-empty">{t("No published versions yet.")}</p>;
  const changed = parts.filter((p) => p.added || p.removed).length;

  return (
    <div className="stack-sm" data-testid="version-history">
      <p className="text-sm muted">{t("Published versions: {n}", { n: all.filter((v) => v.version !== "draft").length })}. {left === right ? t("Pick two versions to compare.") : t("Changed blocks between them: {n}", { n: changed })}</p>
      <div className="grid md:grid-cols-2 gap-3">
        {[[left, setLeft, "left", "Older"], [right, setRight, "right", "Newer"]].map(([val, set, side, name]) => (
          <div key={side} className="stack-sm">
            <span className="mono-label">{t(name)}</span>
            <select className="field text-sm" value={val} onChange={(e) => set(Number(e.target.value))} data-testid={`version-select-${side}`}>
              {all.map((v, i) => <option key={i} value={i}>{label(v)}</option>)}
            </select>
            <Approval v={all[val]} />
            <DiffPane parts={parts} side={side} />
          </div>
        ))}
      </div>
    </div>
  );
}
